import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { getUserId } from '../services/auth';
import { LocationsEditor, type LocationRow } from './LocationsEditor';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import type { FlatSearch } from '../types/api';

interface CreateSearchWizardProps {
  onComplete: (search: FlatSearch) => void;
  onCancel: () => void;
}

type Step = 1 | 2 | 3 | 4;

export function CreateSearchWizard({ onComplete, onCancel }: CreateSearchWizardProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(1);

  // Step 1: Search name
  const [searchName, setSearchName] = useState('');

  // Step 2: Locations
  const [locations, setLocations] = useState<LocationRow[]>([]);

  // Step 3: Notifications
  const [emailNotifications, setEmailNotifications] = useState(true);

  // Step 4: Creating
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const canAdvanceFromStep1 = searchName.trim().length > 0;

  const handleNext = useCallback(() => {
    setStep((s) => Math.min(s + 1, 4) as Step);
  }, []);

  const handleBack = useCallback(() => {
    setStep((s) => Math.max(s - 1, 1) as Step);
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      // 1. Create the search
      const search = await api.createSearch(searchName.trim());

      // 2. Save locations (if any were added)
      const filled = locations.filter((l) => l.name.trim() && l.address.trim());
      if (filled.length > 0) {
        await api.updateLocations(
          search.id,
          filled.map(({ name, address, isPrimary }) => ({ name, address, isPrimary })),
        );
      }

      // 3. Save notification preference
      const userId = await getUserId();
      if (userId) {
        await api.updateMemberPreferences(search.id, userId, {
          emailNotificationsEnabled: emailNotifications,
        });
      }

      onComplete(search);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('app.createSearchWizard.alreadyHasSearch'));
      } else {
        setError(t('app.createSearchWizard.failedToCreate'));
      }
    } finally {
      setCreating(false);
    }
  };

  const stepIndicator = (
    <div className="wizard-steps">
      {([1, 2, 3, 4] as Step[]).map((s) => (
        <div key={s} className={`wizard-step-dot ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}>
          {s < step ? <Check size={12} /> : s}
        </div>
      ))}
    </div>
  );

  return (
    <div className="wizard">
      {stepIndicator}

      <div className="wizard-body">
        {/* ─── Step 1: Search Name ─── */}
        {step === 1 && (
          <>
            <h3 className="wizard-step-title">{t('app.createSearchWizard.step1Title')}</h3>
            <p className="wizard-step-desc">{t('app.createSearchWizard.step1Desc')}</p>
            <input
              className="input wizard-name-input"
              type="text"
              placeholder={t('app.createSearchWizard.namePlaceholder')}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canAdvanceFromStep1) handleNext(); }}
              autoFocus
            />
          </>
        )}

        {/* ─── Step 2: Locations ─── */}
        {step === 2 && (
          <>
            <h3 className="wizard-step-title">{t('app.createSearchWizard.step2Title')}</h3>
            <p className="wizard-step-desc">
              {t('app.createSearchWizard.step2Desc')}
            </p>
            <LocationsEditor
              searchId=""
              standalone={false}
              locations={locations}
              onLocationsChange={setLocations}
            />
          </>
        )}

        {/* ─── Step 3: Notifications ─── */}
        {step === 3 && (
          <>
            <h3 className="wizard-step-title">{t('app.createSearchWizard.step3Title')}</h3>
            <p className="wizard-step-desc">
              {t('app.createSearchWizard.step3Desc')}
            </p>
            <div className="wizard-notif-toggle">
              <button
                role="switch"
                aria-checked={emailNotifications}
                className={`settings-toggle ${emailNotifications ? 'on' : ''}`}
                onClick={() => setEmailNotifications((v) => !v)}
              >
                <span className="settings-toggle-thumb" />
              </button>
              <span className="wizard-notif-label">
                {emailNotifications ? t('app.createSearchWizard.notifEnabled') : t('app.createSearchWizard.notifDisabled')}
              </span>
            </div>
          </>
        )}

        {/* ─── Step 4: Summary ─── */}
        {step === 4 && (
          <>
            <h3 className="wizard-step-title">{t('app.createSearchWizard.step4Title')}</h3>
            <div className="wizard-summary">
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">{t('app.createSearchWizard.summaryName')}</span>
                <span className="wizard-summary-value">{searchName}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">{t('app.createSearchWizard.summaryLocations')}</span>
                <span className="wizard-summary-value">
                  {locations.filter((l) => l.name.trim() && l.address.trim()).length > 0
                    ? locations.filter((l) => l.name.trim() && l.address.trim()).map((l) => l.name).join(', ')
                    : t('app.createSearchWizard.noneAdded')}
                </span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">{t('app.createSearchWizard.summaryNotifications')}</span>
                <span className="wizard-summary-value">{emailNotifications ? t('common.enabled') : t('common.disabled')}</span>
              </div>
            </div>
            {error && <p className="wizard-error">{error}</p>}
          </>
        )}
      </div>

      {/* ─── Navigation ─── */}
      <div className="wizard-nav">
        {step === 1 ? (
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>{t('common.cancel')}</button>
        ) : (
          <button className="btn btn-ghost btn-sm" onClick={handleBack} disabled={creating}>
            <ArrowLeft size={16} /> {t('common.back')}
          </button>
        )}

        <div className="wizard-nav-right">
          {(step === 2 || step === 3) && (
            <button className="btn btn-ghost btn-sm" onClick={handleNext}>{t('common.skip')}</button>
          )}
          {step < 4 && (
            <button className="btn btn-primary btn-sm" onClick={handleNext} disabled={step === 1 && !canAdvanceFromStep1}>
              {t('common.next')} <ArrowRight size={16} />
            </button>
          )}
          {step === 4 && (
            <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={creating}>
              {creating ? t('app.createSearchWizard.creating') : t('app.createSearchWizard.createSearch')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
